/**
 * @fileoverview Invitation acceptance page for organization invitations.
 * Handles three flows: new user registration, existing authenticated user join,
 * and existing unauthenticated user login prompt.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Loading from '../components/Loading';
import PasswordInput from '../components/PasswordInput';

/**
 * AcceptInvitation page component.
 * Reads `?token=` from URL, fetches invitation details, and shows the
 * appropriate UI based on user state:
 * - New user: registration form with email pre-filled
 * - Existing user (authenticated): "Join" button
 * - Existing user (not authenticated): "Log in first" prompt
 *
 * @returns {JSX.Element} The invitation acceptance page
 */
function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');

  // Registration form fields (for new users)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    /**
     * Fetch invitation details from the backend.
     */
    async function fetchInvitation() {
      try {
        const response = await api.getInvitationDetails(token);
        setInvitation(response.data);
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) {
          setError('This invitation was not found.');
        } else if (status === 410) {
          setError(err.response?.data?.error || 'This invitation has expired or been used.');
        } else {
          setError('Failed to load invitation details.');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchInvitation();
  }, [token]);

  /**
   * Handle "Join" button for authenticated existing users.
   */
  const handleJoin = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await api.acceptInvitation({ token });
      setSuccess(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle registration form submit for new users.
   *
   * @param {Event} e - Form submit event
   */
  const handleRegister = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setSubmitting(false);
      return;
    }

    try {
      const response = await api.acceptInvitation({
        token,
        email: invitation.email,
        password,
        confirm_password: confirmPassword,
        first_name: firstName,
        last_name: lastName,
      });
      setSuccess(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Loading variant="spinner" size="sm" message="Loading invitation..." />
        </div>
      </div>
    );
  }

  // Error state (no invitation)
  if (error && !invitation) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2 className="auth-card__title">Invitation</h2>
          <div className="err-msg" role="alert">{error}</div>
          <p className="auth-card__footer">
            <Link to="/landing" className="btn btn-ghost">Go to homepage</Link>
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2 className="auth-card__title">Invitation Accepted</h2>
          <div className="auth-card__success" role="status">{success}</div>
          <p className="auth-card__footer">
            {invitation.requires_registration ? (
              <Link to="/login" className="btn btn-primary">Log in to get started</Link>
            ) : (
              <Link to="/" className="btn btn-primary">Go to dashboard</Link>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <a href="https://cogslog.com" aria-label="COGSLOG home">
          <img
            src="/logos/stacked/cogslog-stacked-tagline-color.svg"
            alt="COGSLOG"
            className="auth-logo"
          />
        </a>
        <h2 className="auth-card__title">Organization Invitation</h2>

        <p className="auth-card__description">
          You&apos;ve been invited to join{' '}
          <strong>{invitation.organization_name}</strong> as{' '}
          <strong>{invitation.role}</strong>
        </p>

        {error && <div id="accept-invitation-error" className="err-msg" role="alert">{error}</div>}

        {/* Flow 1: Existing user, authenticated — show Join button */}
        {!invitation.requires_registration && authenticated && (
          <div className="auth-card__actions">
            <button
              className="btn btn-primary auth-card__full-btn"
              onClick={handleJoin}
              disabled={submitting}
            >
              {submitting ? 'Joining...' : `Join ${invitation.organization_name}`}
            </button>
          </div>
        )}

        {/* Flow 2: Existing user, not authenticated — prompt login */}
        {!invitation.requires_registration && !authenticated && (
          <div className="auth-card__actions">
            <p className="auth-card__description">
              You already have an account. Please log in to accept this invitation.
            </p>
            <Link
              to={`/login?redirect=${encodeURIComponent(`/accept-invitation?token=${token}`)}`}
              className="btn btn-primary auth-card__full-btn"
            >
              Log in to continue
            </Link>
          </div>
        )}

        {/* Flow 3: New user — registration form */}
        {invitation.requires_registration && (
          <form onSubmit={handleRegister}>
            <div className="field">
              <label htmlFor="invite-email" className="label">Email</label>
              <input
                id="invite-email"
                type="email"
                className="input"
                value={invitation.email}
                readOnly
              />
            </div>
            <div className="field">
              <label htmlFor="invite-first-name" className="label">First Name</label>
              <input
                id="invite-first-name"
                type="text"
                className="input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                maxLength={100}
                aria-invalid={!!error}
                aria-describedby={error ? 'accept-invitation-error' : undefined}
              />
            </div>
            <div className="field">
              <label htmlFor="invite-last-name" className="label">Last Name</label>
              <input
                id="invite-last-name"
                type="text"
                className="input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                maxLength={100}
                aria-invalid={!!error}
                aria-describedby={error ? 'accept-invitation-error' : undefined}
              />
            </div>
            <div className="field">
              <label htmlFor="invite-password" className="label">Password</label>
              <PasswordInput
                id="invite-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                aria-invalid={!!error}
                aria-describedby={error ? 'accept-invitation-error' : undefined}
              />
            </div>
            <div className="field">
              <label htmlFor="invite-confirm-password" className="label">Confirm Password</label>
              <PasswordInput
                id="invite-confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                aria-invalid={!!error}
                aria-describedby={error ? 'accept-invitation-error' : undefined}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary auth-card__full-btn"
              disabled={submitting}
            >
              {submitting ? 'Creating account...' : 'Create Account & Join'}
            </button>
          </form>
        )}

        <p className="auth-card__meta">
          Invited by {invitation.inviter_name}
        </p>
      </div>
    </div>
  );
}

export default AcceptInvitation;
